import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppShell } from '../components/AppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Microphone, Keyboard, Camera, ClipboardText, Eye,
  MapPin, CalendarBlank, FilePdf, FunnelSimple, Buildings,
  Binoculars, FileText, ShieldCheck, HardHat,
} from '@phosphor-icons/react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { SendDocumentEmailDialog } from '../components/documents/SendDocumentEmailDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_CONFIG = {
  text:        { label: 'Note',        color: '#7C3AED', bg: 'bg-violet-100', text: 'text-violet-700', icon: Keyboard },
  voice:       { label: 'Vocal',       color: '#FF6B00', bg: 'bg-orange-100', text: 'text-orange-700', icon: Microphone },
  photo:       { label: 'Photo',       color: '#0055FF', bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Camera },
  reserve:     { label: 'Reserve',     color: '#10B981', bg: 'bg-emerald-100',text: 'text-emerald-700', icon: ClipboardText },
  observation: { label: 'Securite',    color: '#EF4444', bg: 'bg-red-100',    text: 'text-red-700',    icon: ShieldCheck },
  visite:      { label: 'Visite CSPS', color: '#F59E0B', bg: 'bg-amber-100',  text: 'text-amber-700',  icon: Binoculars },
  document:    { label: 'Document',    color: '#6366F1', bg: 'bg-indigo-100',  text: 'text-indigo-700', icon: FileText },
  inspection:  { label: 'Inspection',  color: '#14B8A6', bg: 'bg-teal-100',   text: 'text-teal-700',   icon: HardHat },
  inspection_trace: { label: 'Inspection CSPS', color: '#0F766E', bg: 'bg-teal-50', text: 'text-teal-700', icon: HardHat },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
}

function formatDay(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}

function JournalEntry({ entry }) {
  const cfg = TYPE_CONFIG[entry.type] || TYPE_CONFIG.text;
  const Icon = cfg.icon;

  return (
    <div className="flex gap-3 group" data-testid={`journal-entry-${entry.id}`}>
      {/* Timeline dot + line */}
      <div className="flex flex-col items-center flex-shrink-0 w-8">
        <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center ring-2 ring-white`}>
          <Icon size={14} className={cfg.text} weight="bold" />
        </div>
        <div className="w-px flex-1 bg-gray-200 mt-1" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-5 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-xs font-bold uppercase tracking-wide ${cfg.text}`}>{cfg.label}</span>
          <span className="text-xs text-gray-400">{formatDate(entry.date)}</span>
          {entry.author && <span className="text-xs text-gray-400">— {entry.author}</span>}
        </div>

        <Card className="border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <p className="text-sm text-[#111827] leading-relaxed">{entry.content}</p>
            {entry.detail && entry.type === 'reserve' && (
              <p className="text-xs text-gray-500 mt-1.5">{entry.detail}</p>
            )}
            {entry.type === 'reserve' && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {entry.location && (
                  <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    <MapPin size={10} /> {entry.location}
                  </span>
                )}
                {entry.priority && (
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                    entry.priority === 'high' ? 'bg-red-100 text-red-700' :
                    entry.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{entry.priority === 'high' ? 'Haute' : entry.priority === 'medium' ? 'Moyenne' : 'Basse'}</span>
                )}
                {entry.status && (
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                    entry.status === 'open' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>{entry.status === 'open' ? 'Ouverte' : 'Levee'}</span>
                )}
              </div>
            )}
            {entry.type === 'observation' && entry.severity && (
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                  entry.severity === 'high' || entry.severity === 'critical' ? 'bg-red-100 text-red-700' :
                  entry.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{entry.severity === 'high' || entry.severity === 'critical' ? 'Critique' : entry.severity === 'medium' ? 'Moderee' : 'Faible'}</span>
              </div>
            )}
            {entry.type === 'visite' && (
              <div className="flex gap-2 mt-2">
                {entry.visite_type && <span className="text-xs px-2 py-0.5 rounded font-medium bg-amber-100 text-amber-700">{entry.visite_type}</span>}
                {entry.status && <span className="text-xs px-2 py-0.5 rounded font-medium bg-gray-100 text-gray-600">{entry.status}</span>}
              </div>
            )}
            {entry.type === 'document' && entry.detail && (
              <div className="flex gap-2 mt-2">
                <span className="text-xs px-2 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">{entry.detail}</span>
              </div>
            )}
            {entry.type === 'inspection_trace' && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <a
                  href="/csps/inspections"
                  className="text-xs px-2 py-0.5 rounded font-medium bg-teal-100 text-teal-700 hover:bg-teal-200 transition"
                  data-testid={`journal-inspection-link-${entry.inspection_id || entry.id}`}>
                  Voir dans Inspections
                </a>
                {entry.statut && (
                  <span className="text-xs px-2 py-0.5 rounded font-medium bg-gray-100 text-gray-600">
                    {entry.statut}
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function QuickNoteInput({ chantierId, onSaved }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/notes`, { text: text.trim(), chantier_id: chantierId }, { withCredentials: true });
      setText('');
      toast.success('Note ajoutée');
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex gap-2 items-end" data-testid="journal-quick-note">
      <Textarea
        value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Ajouter une note rapide..."
        className="min-h-[44px] max-h-[120px] text-sm resize-none flex-1"
        data-testid="journal-note-input"
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } }}
      />
      <Button onClick={save} disabled={!text.trim() || saving} size="icon"
        className="h-11 w-11 bg-[#FF6B00] hover:bg-orange-700 flex-shrink-0"
        data-testid="journal-note-send">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </Button>
    </div>
  );
}

function IntegrityBadge({ chantierId }) {
  const [integrity, setIntegrity] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/chantiers/${chantierId}/journal/integrity`, { withCredentials: true })
      .then(res => setIntegrity(res.data))
      .catch(() => {});
  }, [chantierId]);

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/chantiers/${chantierId}/journal/certificate`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Certificat_Integrite.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur certificat'); }
    finally { setDownloading(false); }
  };

  if (!integrity || integrity.total === 0) return null;

  return (
    <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs ${
      integrity.valid ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
    }`} data-testid="integrity-badge">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className={integrity.valid ? 'text-emerald-600' : 'text-red-600'} weight="fill" />
        <span className={`font-semibold ${integrity.valid ? 'text-emerald-700' : 'text-red-700'}`}>
          {integrity.valid ? 'Integrite verifiee' : 'Chaine compromise'}
        </span>
        <span className="text-gray-500">{integrity.verified}/{integrity.total} signees</span>
      </div>
      <button onClick={downloadCertificate} disabled={downloading}
        className="text-xs font-medium text-[#FF6B00] hover:underline flex-shrink-0"
        data-testid="download-certificate">
        {downloading ? '...' : 'Certificat'}
      </button>
    </div>
  );
}


export function ChantierJournalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [journal, setJournal] = useState({ chantier: null, entries: [], total: 0 });
  const [filterType, setFilterType] = useState('all');
  const [filterLot, setFilterLot] = useState('all');
  const [lots, setLots] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const loadJournal = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('entry_type', filterType);
      if (filterLot !== 'all') params.append('lot_id', filterLot);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/chantiers/${id}/journal?${params}`, { withCredentials: true });
      setJournal(res.data);
    } catch { toast.error('Erreur chargement journal'); }
    finally { setLoading(false); }
  }, [id, filterType, filterLot, dateFrom, dateTo]);

  const loadLots = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chantiers/${id}/lots`, { withCredentials: true });
      setLots(res.data || []);
    } catch { setLots([]); }
  }, [id]);

  useEffect(() => { loadJournal(); }, [loadJournal]);
  useEffect(() => { loadLots(); }, [loadLots]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('entry_type', filterType);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/chantiers/${id}/journal/pdf?${params}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Journal_${journal.chantier?.name || 'chantier'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur export PDF'); }
    finally { setExporting(false); }
  };

  // Group entries by day
  const grouped = {};
  (journal.entries || []).forEach(e => {
    const day = e.date ? e.date.slice(0, 10) : 'unknown';
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(e);
  });
  const days = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  // Stats
  const stats = Object.fromEntries(Object.keys(TYPE_CONFIG).map(k => [k, 0]));
  (journal.entries || []).forEach(e => { if (stats[e.type] !== undefined) stats[e.type]++; });

  if (loading) return (
    <AppShell><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div></AppShell>
  );

  const chantier = journal.chantier;

  return (
    <AppShell>
      <div className="space-y-4 max-w-3xl mx-auto" data-testid="journal-page">
        {/* Header */}
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 flex-shrink-0 mt-0.5"
            onClick={() => navigate('/chantiers')} data-testid="journal-back">
            <ArrowLeft size={22} />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827] truncate" data-testid="journal-title">
              Journal de chantier
            </h1>
            {chantier && (
              <div className="flex items-center gap-2 text-sm text-[#4B5563] mt-0.5 flex-wrap">
                <Buildings size={14} className="flex-shrink-0" />
                <span className="font-medium truncate">{chantier.name}</span>
                {chantier.address && <><span className="text-gray-300">|</span><span className="truncate">{chantier.address}</span></>}
              </div>
            )}
          </div>
          <Button onClick={exportPdf} disabled={exporting} variant="outline"
            className="h-10 text-[#FF6B00] border-orange-200 hover:bg-orange-50 flex-shrink-0"
            data-testid="journal-export-pdf">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FilePdf size={18} className="mr-1" />}
            <span className="hidden sm:inline">PDF</span>
          </Button>
        </div>

        {/* Integrity badge */}
        <IntegrityBadge chantierId={id} />

        {/* Stats bar */}
        <div className="flex gap-2 flex-wrap" data-testid="journal-stats">
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
            const count = stats[key] || 0;
            if (count === 0) return null;
            const Icon = cfg.icon;
            return (
              <button key={key} onClick={() => setFilterType(filterType === key ? 'all' : key)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-all ${
                  filterType === key ? `${cfg.bg} ${cfg.text} border-current` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`} data-testid={`filter-${key}`}>
                <Icon size={12} weight="bold" /> {cfg.label} ({count})
              </button>
            );
          })}
          {filterType !== 'all' && (
            <button onClick={() => setFilterType('all')}
              className="text-xs text-gray-500 underline ml-1" data-testid="filter-clear">
              Tout afficher
            </button>
          )}
        </div>

        {/* Date filters */}
        <div className="flex gap-2 items-center flex-wrap">
          <FunnelSimple size={16} className="text-gray-400 flex-shrink-0" />
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 w-[140px] text-xs" placeholder="Du" data-testid="filter-date-from" />
          <span className="text-xs text-gray-400">au</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="h-9 w-[140px] text-xs" placeholder="Au" data-testid="filter-date-to" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-xs text-gray-500 underline" data-testid="filter-date-clear">
              Reinitialiser
            </button>
          )}
          {lots.length > 0 && (
            <Select value={filterLot} onValueChange={setFilterLot}>
              <SelectTrigger className="h-9 w-[170px] text-xs" data-testid="filter-lot">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="all">Tous les lots</SelectItem>
                {lots.map(l => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.code ? `${l.code} - ` : ''}{l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Quick note input */}
        <QuickNoteInput chantierId={id} onSaved={loadJournal} />

        {/* Timeline */}
        <div className="space-y-1" data-testid="journal-timeline">
          {days.length > 0 ? days.map(day => (
            <div key={day}>
              <div className="flex items-center gap-3 my-3">
                <CalendarBlank size={14} className="text-gray-400" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{formatDay(day)}</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              {grouped[day].map(entry => (
                <JournalEntry key={entry.id} entry={entry} />
              ))}
            </div>
          )) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <CalendarBlank size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-500">Aucune entree dans le journal</p>
                <p className="text-sm text-gray-400 mt-1">Ajoutez une note ci-dessus pour commencer</p>
              </CardContent>
            </Card>
          )}
        </div>

        {journal.total > 0 && (
          <p className="text-xs text-center text-gray-400 py-2" data-testid="journal-total">
            {journal.total} entree(s) au total
          </p>
        )}
      </div>

      <SendDocumentEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        docType="journal"
        pdfUrl={`${API}/chantiers/${id}/journal/pdf`}
        filename={`Journal_${(journal.chantier?.name || 'chantier').replace(/[^a-z0-9]/gi,'_')}.pdf`}
        subject={`[CHANTIX] Journal chantier — ${journal.chantier?.name || ''}`}
        docTitle={`Journal du chantier ${journal.chantier?.name || ''}`}
        suggestedRecipients={[]}
      />
    </AppShell>
  );
}

